import { useRef, useState } from 'react';
import { doIfDefined } from '../../helpers/general-helper/general-helper';

export const useMobileCarousel = () => {
	const carouselContainerRef = useRef<HTMLDivElement>(null);
	const touchStartXRef = useRef<number>(0);
	const touchMoveXRef = useRef<number>(0);
	const touchEndXRef = useRef<number>(0);
	// offset x in percentage
	const [offsetX, setOffsetX] = useState<number>(0);
	// Safari returns different values of scrollWidth, which causes weird behavior when swiping using both touch and arrow buttos
	// that's why it is needed to be set once
	const minOffsetXRef = useRef<number | null>(null);
	// this is needed for when both touch and buttons are available (iPad landscape)
	const [transitionSpeed, setTransitionSpeed] = useState<string>('all 0.05s linear');

	const verifyOffsetValue = (offset: number, shouldUpdateTouchEndRef: boolean) => {
		doIfDefined(carouselContainerRef.current, (carouselContainer) => {
			// set the value once
			let minOffsetX = minOffsetXRef.current;
			if (!minOffsetX) {
				minOffsetXRef.current = carouselContainer.offsetWidth - carouselContainer.scrollWidth;
				minOffsetX = minOffsetXRef.current;
			}
			if (minOffsetX) {
				let verifiedOffset = offset;
				const maxOffsetX = 0;
				// locks carousel at the begining and end, if the offset is bigger than the max, then stop at the max and viceversa for the min
				// this may look reversed, but it's intentional because of the touch swipe gesture
				if (offset > maxOffsetX) {
					verifiedOffset = maxOffsetX;
				}
				if (offset < minOffsetX) {
					verifiedOffset = minOffsetX;
				}
				setOffsetX(verifiedOffset);
				if (shouldUpdateTouchEndRef) {
					touchEndXRef.current = verifiedOffset;
				}
			}
		});
	};

	const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
		// touch transition needs to be faster with linear to keep up with the speed of the finger
		setTransitionSpeed('all 0.05s linear');
		touchStartXRef.current = e.targetTouches[0].clientX;
		touchMoveXRef.current = e.targetTouches[0].clientX;
	};

	const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
		touchMoveXRef.current = e.targetTouches[0].clientX;
		const translateDist = touchEndXRef.current + (touchMoveXRef.current - touchStartXRef.current);
		verifyOffsetValue(translateDist, false);
	};

	const handleTouchEnd = () => {
		// saves last position of the touch swipe for the next time
		touchEndXRef.current = offsetX;
	};

	return {
		carouselContainerRef,
		handleTouchEnd,
		handleTouchMove,
		handleTouchStart,
		offsetX,
		transitionSpeed,
		setTransitionSpeed,
		verifyOffsetValue
	};
};
